import { generateUniqueId } from "helpers/generic";
import { AppAction } from "redux/store";

import notificationReducer from "redux/reducers/notificationReducer";
import teamsReducer from "redux/reducers/teamsReducer";

import {
 IContactData,
 ITeamsApi,
 ITeamsUserSettings,
 IUserPreferences
} from "types/api/teamsApiInterface";
import { settingsType } from "types/global/user";

import { ApiService } from "service/ApiService";

export const getAllSecondLevelUsers =
 (
  currentIndex: number,
  sizePerPage: number,
  parentid: number,
  q?: string
 ): AppAction<Promise<void>> =>
 async (dispatch) => {
  try {
   const { data } = await ApiService<ITeamsApi>(
    `/teams/allteams?currentIndex=${currentIndex}?sizePerPage=${sizePerPage}?q=${q}`
   );
   dispatch(teamsReducer.actions.setTeams(data || {}));
  } catch (error) {
   console.warn("FAILED TO GET TEAMS");
  }
 };

export const getAllUserContacts =
 (): AppAction<Promise<Array<IContactData>>> => async (dispatch) => {
  try {
   const { data } = await ApiService<Array<IContactData>>("/contact/getuserscontactslist", "POST");
   return data;
  } catch (error) {
   console.warn("FAILED TO GET TEAMS");
   return [];
  }
 };

export const postAddTeamsUser =
 (userid: number, email: string, password: string, contact_id: number): AppAction<Promise<void>> =>
 async (dispatch) => {
  try {
   const { data } = await ApiService("/teams/addteams", "POST", {
    email,
    password,
    contact_id
   });
   if (data) {
    dispatch(getAllSecondLevelUsers(0, 10, userid));
    dispatch(
     notificationReducer.actions.addAlert({
      id: generateUniqueId(),
      message: "User created successfully",
      timestamp: Date.now(),
      type: "success"
     })
    );
   }
  } catch (error: any) {
   dispatch(
    notificationReducer.actions.addAlert({
     id: generateUniqueId(),
     message: `Failed to create user - ${error?.message || "unknown error"}`,
     timestamp: Date.now(),
     type: "error"
    })
   );
  }
 };

export const putUpdateTeamsUser =
 (id: number, email: string, userid: number): AppAction<Promise<void>> =>
 async (dispatch) => {
  try {
   const { data } = await ApiService(`/teams/updateteams/${id}`, "PUT", {
    email
   });
   if (data) {
    dispatch(getAllSecondLevelUsers(0, 10, userid));
    dispatch(
     notificationReducer.actions.addAlert({
      id: generateUniqueId(),
      message: "User updated successfully",
      timestamp: Date.now(),
      type: "success"
     })
    );
   }
  } catch (error: any) {
   dispatch(
    notificationReducer.actions.addAlert({
     id: generateUniqueId(),
     message: `Failed to update user - ${error?.message || "unknown error"}`,
     timestamp: Date.now(),
     type: "error"
    })
   );
  }
 };

export const putUpdateTeamsPassword =
 (id: number, oldPassword: string, newPassword: string, userid: number): AppAction<Promise<void>> =>
 async (dispatch) => {
  try {
   const { data } = await ApiService(`/teams/updatepassword/${id}`, "PUT", {
    oldPassword,
    newPassword
   });
   if (data) {
    dispatch(getAllSecondLevelUsers(0, 10, userid));
    dispatch(
     notificationReducer.actions.addAlert({
      id: generateUniqueId(),
      message: "User updated successfully",
      timestamp: Date.now(),
      type: "success"
     })
    );
   }
  } catch (error: any) {
   dispatch(
    notificationReducer.actions.addAlert({
     id: generateUniqueId(),
     message: `Failed to update user - ${error?.message || "unknown error"}`,
     timestamp: Date.now(),
     type: "error"
    })
   );
  }
 };

export const postDeleteTeamsUser =
 (id: number, userid: number): AppAction<Promise<void>> =>
 async (dispatch) => {
  try {
   const { data } = await ApiService("/teams/deleteteams", "POST", {
    id
   });
   if (data) {
    dispatch(getAllSecondLevelUsers(0, 10, userid));
    dispatch(
     notificationReducer.actions.addAlert({
      id: generateUniqueId(),
      message: "User deleted successfully",
      timestamp: Date.now(),
      type: "success"
     })
    );
   }
  } catch (error: any) {
   dispatch(
    notificationReducer.actions.addAlert({
     id: generateUniqueId(),
     message: `Failed to delete user - ${error?.message || "unknown error"}`,
     timestamp: Date.now(),
     type: "error"
    })
   );
  }
 };

export const getUserPreferences =
 (): AppAction<Promise<IUserPreferences | null>> => async (dispatch) => {
  try {
   const { data } = await ApiService<IUserPreferences>("/teams/getsitepreferences");
   return data;
  } catch (error: any) {
   dispatch(
    notificationReducer.actions.addAlert({
     id: generateUniqueId(),
     message: `Failed to get preferences - ${error?.message || "unknown error"}`,
     timestamp: Date.now(),
     type: "error"
    })
   );
   return null;
  }
 };

export const postChangeUserPreferences =
 (image: string, color: string): AppAction<Promise<void>> =>
 async (dispatch) => {
  try {
   const { data } = await ApiService("/teams/setsitepreferences", "POST", {
    image,
    color
   });
   if (data) {
    dispatch(
     notificationReducer.actions.addAlert({
      id: generateUniqueId(),
      message: "Changing user preferences",
      timestamp: Date.now(),
      type: "success"
     })
    );
   }
  } catch (error: any) {
   dispatch(
    notificationReducer.actions.addAlert({
     id: generateUniqueId(),
     message: `Failed to change preferences - ${error?.message || "unknown error"}`,
     timestamp: Date.now(),
     type: "error"
    })
   );
  }
 };

export const getUserSettings =
 (id: number): AppAction<Promise<ITeamsUserSettings | null>> =>
 async (dispatch) => {
  try {
   const { data } = await ApiService<ITeamsUserSettings>("/teams/getdatas", "POST", {
    teams_id: id
   });
   return data;
  } catch (error: any) {
   dispatch(
    notificationReducer.actions.addAlert({
     id: generateUniqueId(),
     message: `Failed to get user settings - ${error?.message || "unknown error"}`,
     timestamp: Date.now(),
     type: "error"
    })
   );
   return null;
  }
 };

export const postChangeUserSettings =
 (id: number, datas: settingsType): AppAction<Promise<void>> =>
 async (dispatch) => {
  try {
   const { data } = await ApiService("/teams/setpreferences", "POST", {
    teams_id: id,
    domains: datas.domains,
    servers: datas.servers,
    showAuthinfo: datas.showAuthinfo,
    showDns: datas.showDns,
    showOwners: datas.showOwners,
    showNs: datas.showNs,
    editAuthinfo: datas.editAuthinfo,
    editDns: datas.editDns,
    editNs: datas.editNs,
    editRebootServer: datas.editRebootServer,
    editPowerOffOnServer: datas.editPowerOffOnServer,
    sites: datas.sites,
    mails: datas.mails,
    zones: datas.zones,
    showBackupServer: datas.showBackupServer,
    editBackupServer: datas.editBackupServer,
    editRebuildServer: datas.editRebuildServer,
    editPowerOffOnSite: datas.editPowerOffOnSite,
    editPowerOffOnMail: datas.editPowerOffOnMail,
    editPowerOffOnZone: datas.editPowerOffOnZone,
    showZoneRecords: datas.showZoneRecords,
    editZoneRecords: datas.editZoneRecords,
    cloudboxes: datas.cloudboxes,
    networks: datas.networks,
    firewalls: datas.firewalls,
    floating_ips: datas.floating_ips,
    load_balancers: datas.load_balancers,
    snapshots: datas.snapshots,
    volumes: datas.volumes,
    showMailDetails: datas.showMailDetails,
    editMailDetails: datas.editMailDetails,
    showMailbox: datas.showMailbox,
    editMailbox: datas.editMailbox,
    showAutomaticMail: datas.showAutomaticMail,
    editAutomaticMail: datas.editAutomaticMail
   });
   if (data) {
    dispatch(
     notificationReducer.actions.addAlert({
      id: generateUniqueId(),
      message: "Changing user preferences",
      timestamp: Date.now(),
      type: "success"
     })
    );
   }
  } catch (error: any) {
   dispatch(
    notificationReducer.actions.addAlert({
     id: generateUniqueId(),
     message: `Failed to change preferences - ${error?.message || "unknown error"}`,
     timestamp: Date.now(),
     type: "error"
    })
   );
  }
 };
